import type { Id } from '@freightapi/core/ids'
import { type IDBPDatabase, openDB } from 'idb'
import type { CreateRemarkBody, DrawnSubmission } from './sign'

/**
 * The durable half of the offline-light queue (M2-08): what a submission looks like on disk, and
 * the handful of IndexedDB calls that put it there.
 *
 * It is split from the runner in `offline-queue.ts` on purpose. Everything here is storage and
 * arithmetic — no `fetch`, no React, no timers — so the ordering rule, the content hash and the
 * backoff can be tested without a browser, and the runner has one place to reach for the disk.
 */

const DB_NAME = 'freightapi-sign'
const DB_VERSION = 1
const STORE = 'submissions'

/** What a queued submission is. The order of this list is the order they leave the device in. */
export const QUEUE_KINDS = ['photo', 'remark', 'signature'] as const

export type QueueKind = (typeof QUEUE_KINDS)[number]

/**
 * Where an item is.
 *
 * `conflict` and `failed` are both terminal, and they are two states rather than one because they
 * mean opposite things to the person holding the phone: a conflict says somebody else finished the
 * errand, a failure says this submission was refused and is still theirs to deal with.
 */
export type QueueStatus = 'pending' | 'sending' | 'conflict' | 'failed'

/** One photo of a reservation, kept as the bytes that came off the camera after shrinking. */
export interface PhotoPayload {
  readonly blob: Blob
  readonly name: string
}

/**
 * A reservation, and the photos it refers to. The photos are named by queue item, not by document:
 * the ids the API hands out do not exist yet while the device is offline, and they are written back
 * into `documentId` as each photo lands.
 */
export interface RemarkPayload {
  /** The reservation as the driver typed it; `photo_document_ids` is filled in on the way out. */
  readonly body: CreateRemarkBody
  readonly photos: readonly RemarkPhotoRef[]
}

export interface RemarkPhotoRef {
  readonly itemId: string
  /** The content hash of the photo, which is what the reservation's own hash is computed over. */
  readonly hash: string
  /** Set once that photo reached the API; absent while it is still on the queue. */
  readonly documentId?: Id<'doc'>
}

export interface SignaturePayload {
  readonly body: DrawnSubmission
}

export type QueuePayload =
  | ({ readonly kind: 'photo' } & PhotoPayload)
  | ({ readonly kind: 'remark' } & RemarkPayload)
  | ({ readonly kind: 'signature' } & SignaturePayload)

/** Why an item stopped, in terms the queue screen can turn into a sentence. */
export interface QueueError {
  readonly code: string
  readonly detail?: string
}

export interface QueueItem {
  readonly id: string
  /** The sign token the submission belongs to; a phone can hold errands for more than one link. */
  readonly token: string
  readonly kind: QueueKind
  readonly payload: QueuePayload
  /**
   * SHA-256 of what was captured, computed the moment it was captured and checked again on the way
   * out. It is the proof the ticket asks for: the bytes that leave are the bytes that were signed
   * for, and it is shown on the queue screen so a driver can point at it.
   */
  readonly hash: string
  /** Everything of one errand — the photos, the reservation, the signature — shares this. */
  readonly group: string
  readonly createdAt: string
  readonly attempts: number
  readonly status: QueueStatus
  /** Epoch milliseconds; a pending item is not touched before this. */
  readonly nextAttemptAt: number
  /** `token:kind:hash` — the same capture enqueued twice is one item, not two calls. */
  readonly dedupe: string
  readonly error?: QueueError
}

/**
 * Photos before the reservation, the reservation before the signature. Not a preference: a
 * reservation refers to the documents its photos became, and a signed link is spent, so a signature
 * that overtakes the reservation costs the reservation entirely (M1-23, M2-05).
 */
export function rankOf(kind: QueueKind): number {
  return QUEUE_KINDS.indexOf(kind)
}

/**
 * The order the runner works through. Kind first so the rule above holds across errands, then the
 * moment of capture, then the id — which only decides between two items captured in the same
 * millisecond, and decides it the same way on every read.
 */
export function inSendOrder(items: readonly QueueItem[]): readonly QueueItem[] {
  return [...items].sort(
    (left, right) =>
      rankOf(left.kind) - rankOf(right.kind) ||
      left.createdAt.localeCompare(right.createdAt) ||
      left.id.localeCompare(right.id),
  )
}

/** How long a phone waits before trying again, doubling per attempt up to five minutes. */
export const RETRY_BASE_MS = 2000
export const RETRY_CEILING_MS = 300_000
/** After this many refusals the item stops asking and waits for the driver instead. */
export const MAX_ATTEMPTS = 6

export function backoffMs(attempts: number): number {
  if (attempts <= 1) return RETRY_BASE_MS
  return Math.min(RETRY_BASE_MS * 2 ** (attempts - 1), RETRY_CEILING_MS)
}

let database: Promise<IDBPDatabase> | undefined

function db(): Promise<IDBPDatabase> {
  database ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(instance) {
      if (!instance.objectStoreNames.contains(STORE)) {
        const store = instance.createObjectStore(STORE, { keyPath: 'id' })
        // The one lookup that is not a full read: "is this exact capture already queued?".
        store.createIndex('dedupe', 'dedupe', { unique: false })
      }
    },
  })
  return database
}

/** Lets go of the open handle, so a test can delete the database and start from an empty one. */
export async function closeQueueDatabase(): Promise<void> {
  const open = database
  database = undefined
  if (open !== undefined) (await open).close()
}

export async function allItems(): Promise<readonly QueueItem[]> {
  return inSendOrder((await (await db()).getAll(STORE)) as QueueItem[])
}

export async function itemById(id: string): Promise<QueueItem | undefined> {
  return (await (await db()).get(STORE, id)) as QueueItem | undefined
}

/** The item with this capture, whatever state it is in; the caller decides what that means. */
export async function itemByDedupe(dedupe: string): Promise<QueueItem | undefined> {
  return (await (await db()).getFromIndex(STORE, 'dedupe', dedupe)) as QueueItem | undefined
}

export async function putItem(item: QueueItem): Promise<QueueItem> {
  await (await db()).put(STORE, item)
  return item
}

export async function deleteItem(id: string): Promise<void> {
  await (await db()).delete(STORE, id)
}

/** Reads, changes and writes one item in a single pass; a missing item is simply not changed. */
export async function patchItem(
  id: string,
  change: (item: QueueItem) => QueueItem,
): Promise<QueueItem | undefined> {
  const current = await itemById(id)
  if (current === undefined) return undefined
  return await putItem(change(current))
}
